import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Skeleton,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { getMethodLabel } from '../pages/Payment/paymentNormalizer';
import {
  AccountBalanceWallet,
  Close,
  Description,
  PictureAsPdf,
  ReceiptLong,
  RestartAlt,
  Search,
  TableView,
  Tune,
  Visibility,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDispatch } from 'react-redux';
import API from '../services/api';
import MainText from '../components/MainText';
import CustomSnackBar from '../components/CustomSnackBar';
import DatePick from '../components/DatePick';
import { addTab } from '../tabs/tabsSlice';
import { getReceiptDateState } from './providerStatusDates';

const COLORS = ['#2563eb', '#0f766e', '#d97706', '#7c3aed', '#b42318'];

const localDate = (dateValue) => {
  const value = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(value.getTime())) return '';
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
};

const money = (value) =>
  new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const text = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return `${text.slice(8, 10)}/${text.slice(5, 7)}/${text.slice(0, 4)}`;
  }
  return text;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat('es-GT', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const currentStatusColor = {
  paid: 'success',
  partial_after: 'info',
  pending: 'warning',
  void_after: 'default',
};

const financialStatusLabels = {
  PENDING: 'Pendiente',
  PARTIAL: 'Pago parcial',
  AWAITING_RECONCILIATION: 'Esperando conciliacion',
  PAID: 'Pagada',
  VOID: 'Anulada',
};

function Metric({ label, value, tone = '#10173c', icon: Icon }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderTop: `3px solid ${tone}` }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 800, letterSpacing: 0.45 }}>
            {label}
          </Typography>
          <Typography sx={{ mt: 0.45, fontSize: 19, fontWeight: 800, color: tone }} noWrap>
            {value}
          </Typography>
        </Box>
        {Icon ? <Icon sx={{ color: tone, opacity: 0.28, fontSize: 30 }} /> : null}
      </Stack>
    </Paper>
  );
}

function QueryBackdrop({ open }) {
  return (
    <Backdrop open={open} sx={{ zIndex: (theme) => theme.zIndex.modal + 2, bgcolor: 'rgba(8,15,35,.72)' }}>
      <Paper sx={{ width: { xs: '88vw', md: 620 }, p: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={25} />
          <Box>
            <Typography sx={{ fontWeight: 800 }}>Reconstruyendo obligaciones al corte</Typography>
            <Typography variant="caption" color="text.secondary">Calculando saldos y resumen global...</Typography>
          </Box>
        </Stack>
        <Skeleton variant="rounded" height={58} />
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <Skeleton height={28} />
          <Skeleton height={28} width="92%" />
          <Skeleton height={28} width="84%" />
        </Stack>
      </Paper>
    </Backdrop>
  );
}

function MovementList({ title, movements, tone }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 850, color: tone, mb: 1 }}>{title}</Typography>
      {movements.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Sin movimientos en este periodo.</Typography>
      ) : (
        <Stack spacing={1}>
          {movements.map((movement) => (
            <Paper key={movement.id} variant="outlined" sx={{ p: 1.25, borderLeft: `4px solid ${tone}` }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {formatDate(movement.effective_date)} · {movement.label}
                    </Typography>
                    {movement.date_estimated ? <Chip size="small" color="warning" variant="outlined" label="Fecha estimada" /> : null}
                    {movement.classification_only ? <Chip size="small" variant="outlined" label="Solo clasifica ajuste" /> : null}
                    {movement.is_active === false ? <Chip size="small" color="error" variant="outlined" label="Anulado" /> : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Ref. {movement.reference || '-'} · {getMethodLabel(movement.method) || '-'} · Registrado {formatDateTime(movement.registered_at)}
                  </Typography>
                  {movement.confirmed_at ? <Typography variant="caption" color="text.secondary" display="block">Confirmado {formatDateTime(movement.confirmed_at)}</Typography> : null}
                  {movement.cancelled_at ? <Typography variant="caption" color="error.main" display="block">Anulado {formatDateTime(movement.cancelled_at)}</Typography> : null}
                  {movement.comment ? <Typography variant="caption" display="block" sx={{ mt: 0.4 }}>{movement.comment}</Typography> : null}
                  {movement.document ? (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.6 }}>
                      <Chip size="small" variant="outlined" icon={<Description />} label={`Documento ${movement.document.number}`} />
                      {movement.document.file ? <Link href={movement.document.file} target="_blank" rel="noreferrer" variant="caption">Abrir documento</Link> : null}
                      {(movement.document.attachments || []).map((attachment, index) => (
                        <Link key={attachment.id} href={attachment.file} target="_blank" rel="noreferrer" variant="caption">Adjunto {index + 1}</Link>
                      ))}
                    </Stack>
                  ) : null}
                  {(movement.receipts || []).length ? (
                    <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 0.7 }}>
                      {movement.receipts.map((receipt) => (
                        <Chip
                          key={receipt.id}
                          size="small"
                          color="info"
                          variant="outlined"
                          label={`Recibo ${receipt.number} · ${formatDate(receipt.date)} · ${money(receipt.amount)}`}
                          component={receipt.file ? 'a' : 'div'}
                          href={receipt.file || undefined}
                          target={receipt.file ? '_blank' : undefined}
                          clickable={Boolean(receipt.file)}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 850, whiteSpace: 'nowrap' }}>{money(movement.amount)}</Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function DetailDialog({ open, onClose, loading, detail, asOf }) {
  const invoice = detail?.invoice;
  const receiptState = getReceiptDateState(invoice?.receipt_date, asOf);
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ pr: 6 }}>
        {invoice ? `Detalle factura ${invoice.number}${invoice.series ? ` / ${invoice.series}` : ''}` : 'Detalle de factura'}
        <IconButton onClick={onClose} disabled={loading} sx={{ position: 'absolute', right: 10, top: 10 }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#fafbfc' }}>
        {loading ? (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={80} />
            <Skeleton variant="rounded" height={190} />
            <Skeleton variant="rounded" height={160} />
          </Stack>
        ) : null}
        {!loading && invoice ? (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                <Box><Typography variant="caption" color="text.secondary">Proveedor</Typography><Typography sx={{ fontWeight: 750 }}>{invoice.supplier_name}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Fecha de factura</Typography><Typography>{formatDate(invoice.invoice_date)}</Typography></Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Primera fecha de recepción</Typography>
                  <Typography>{receiptState.isMissing ? 'Sin recepción registrada' : formatDate(invoice.receipt_date)}</Typography>
                  {receiptState.isAfterCutoff ? <Chip size="small" color="warning" variant="outlined" label="Posterior al corte" sx={{ mt: 0.4 }} /> : null}
                </Box>
                <Box><Typography variant="caption" color="text.secondary">Fecha de vencimiento</Typography><Typography>{formatDate(invoice.due_date)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Saldo al corte</Typography><Typography sx={{ fontWeight: 850, color: 'error.main' }}>{money(invoice.balance_at_cutoff)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Saldo actual</Typography><Typography sx={{ fontWeight: 850 }}>{money(invoice.current_balance)}</Typography></Box>
              </Stack>
            </Paper>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1 }}>Lineas de factura</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead><TableRow>{['Codigo', 'Producto', 'Formato', 'Cantidad', 'Recibido', 'Precio', 'Total', 'Vencimiento'].map((label) => <TableCell key={label} sx={{ fontWeight: 800 }}>{label}</TableCell>)}</TableRow></TableHead>
                  <TableBody>
                    {(detail.lines || []).map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.product_code}</TableCell><TableCell>{line.product}</TableCell><TableCell>{line.format || '-'}</TableCell>
                        <TableCell>{line.quantity}</TableCell><TableCell>{line.received_quantity}</TableCell><TableCell>{money(line.unit_price)}</TableCell>
                        <TableCell>{money(line.line_total)}</TableCell><TableCell>{formatDate(line.due_date)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 850, mb: 1 }}>Recepciones y bodega</Typography>
              <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                {(detail.receptions || []).length ? detail.receptions.map((note) => (
                  <Chip key={note.id} variant="outlined" color={note.is_active ? 'primary' : 'default'} label={`NR ${note.number} · ${formatDate(note.date)} · ${note.warehouse_name || 'Sin bodega'}`} />
                )) : <Typography variant="body2" color="text.secondary">Sin notas de recepcion.</Typography>}
              </Stack>
            </Box>

            <MovementList title="Movimientos vigentes al corte" movements={detail.movements_at_cutoff || []} tone="#10173c" />
            <MovementList title="Movimientos posteriores al corte" movements={detail.movements_after_cutoff || []} tone="#d97706" />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={loading}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}

export default function InvoiceProviderStatusReport() {
  const dispatch = useDispatch();
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [cutoff, setCutoff] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [charts, setCharts] = useState({});
  const [reportMeta, setReportMeta] = useState({});
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total_items: 0 });
  const [querySnapshot, setQuerySnapshot] = useState(null);
  const [hasConsulted, setHasConsulted] = useState(false);
  const [blockingLoading, setBlockingLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [exporting, setExporting] = useState({ pdf: false, xlsx: false });
  const [exportMode, setExportMode] = useState('complete');
  const [supplierConfigOpen, setSupplierConfigOpen] = useState(false);
  const [supplierConfigSearch, setSupplierConfigSearch] = useState('');
  const [excludedSupplierIds, setExcludedSupplierIds] = useState([]);
  const [pageFilters, setPageFilters] = useState({ supplierId: '', dueFrom: null, dueTo: null, states: [], search: '' });
  const [detailState, setDetailState] = useState({ open: false, loading: false, data: null });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  const showSnack = useCallback((message, severity = 'info') => {
    setSnack({ open: true, message, severity });
  }, []);

  useEffect(() => {
    dispatch(addTab({ label: 'Obligaciones historicas', path: '/invoice/reports/provider-status' }));
  }, [dispatch]);

  useEffect(() => {
    API.get('invoice/suppliers/')
      .then(({ data }) => setSuppliers(Array.isArray(data) ? data : data?.results || []))
      .catch(() => setSuppliers([]));
  }, []);

  const userData = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('userData') || '{}'); } catch (_) { return {}; }
  }, []);
  const activeCompanyId = reportMeta?.company?.id || suppliers[0]?.company_id || userData?.company_id || userData?.company?.id || 'unknown';
  const activeUserId = userData?.id || userData?.user_id || userData?.username || 'unknown';
  const exclusionsStorageKey = `provider-status-exclusions:${activeUserId}:${activeCompanyId}`;

  useEffect(() => {
    if (activeCompanyId === 'unknown') return;
    try {
      const stored = JSON.parse(localStorage.getItem(exclusionsStorageKey) || '[]');
      setExcludedSupplierIds(Array.isArray(stored) ? stored.map(Number).filter(Boolean) : []);
    } catch (_) {
      setExcludedSupplierIds([]);
    }
  }, [activeCompanyId, exclusionsStorageKey]);

  useEffect(() => {
    if (activeCompanyId === 'unknown') return;
    localStorage.setItem(exclusionsStorageKey, JSON.stringify(excludedSupplierIds));
  }, [activeCompanyId, exclusionsStorageKey, excludedSupplierIds]);

  const requestReport = useCallback(async ({ snapshot, page = 1, pageSize = 25, pageChange = false }) => {
    if (!snapshot?.as_of) {
      showSnack('Selecciona una fecha de corte valida.', 'warning');
      return;
    }
    if (pageChange) setPageLoading(true);
    else setBlockingLoading(true);
    try {
      const { data } = await API.get('invoice/reports/provider-status/', {
        params: {
          as_of: snapshot.as_of,
          supplier_id: snapshot.supplier_id || undefined,
          exclude_supplier_ids: snapshot.supplier_id ? undefined : (snapshot.exclude_supplier_ids || []).join(','),
          page,
          page_size: pageSize,
        },
      });
      setRows(Array.isArray(data?.results) ? data.results : []);
      setSummary(data?.summary || {});
      setCharts(data?.charts || {});
      setReportMeta(data?.report || {});
      setPagination(data?.pagination || { page, page_size: pageSize, total_items: 0 });
      setHasConsulted(true);
    } catch (error) {
      setRows([]);
      setSummary({});
      setCharts({});
      setPagination({ page: 1, page_size: pageSize, total_items: 0 });
      showSnack(error?.response?.data?.detail || 'No se pudo consultar el reporte.', 'error');
    } finally {
      setBlockingLoading(false);
      setPageLoading(false);
    }
  }, [showSnack]);

  const handleConsult = () => {
    const snapshot = {
      as_of: localDate(cutoff),
      supplier_id: supplier?.id || null,
      exclude_supplier_ids: supplier ? [] : excludedSupplierIds,
    };
    setQuerySnapshot(snapshot);
    requestReport({ snapshot, page: 1, pageSize: pagination.page_size || 25 });
  };

  const handlePageChange = (_, zeroBasedPage) => {
    if (!querySnapshot) return;
    requestReport({
      snapshot: querySnapshot,
      page: zeroBasedPage + 1,
      pageSize: pagination.page_size,
      pageChange: true,
    });
  };

  const handlePageSizeChange = (event) => {
    const pageSize = Number(event.target.value);
    if (!querySnapshot) return;
    requestReport({ snapshot: querySnapshot, page: 1, pageSize, pageChange: true });
  };

  const openDetail = async (row) => {
    setDetailState({ open: true, loading: true, data: null });
    try {
      const { data } = await API.get(`invoice/reports/provider-status/${row.id}/`, {
        params: {
          as_of: querySnapshot.as_of,
          supplier_id: querySnapshot.supplier_id || undefined,
          exclude_supplier_ids: querySnapshot.supplier_id ? undefined : (querySnapshot.exclude_supplier_ids || []).join(','),
        },
      });
      setDetailState({ open: true, loading: false, data });
    } catch (error) {
      setDetailState({ open: false, loading: false, data: null });
      showSnack(error?.response?.data?.detail || 'No se pudo cargar el detalle.', 'error');
    }
  };

  const exportReport = async (format) => {
    if (!querySnapshot) return;
    const visibleIds = filteredRows.map((row) => row.id);
    if (exportMode === 'visible' && visibleIds.length === 0) return;
    setExporting((prev) => ({ ...prev, [format]: true }));
    try {
      const response = await API.get(`invoice/reports/provider-status/export/${format}/`, {
        params: {
          as_of: querySnapshot.as_of,
          supplier_id: querySnapshot.supplier_id || undefined,
          exclude_supplier_ids: querySnapshot.supplier_id ? undefined : (querySnapshot.exclude_supplier_ids || []).join(','),
          invoice_ids: exportMode === 'visible' ? visibleIds.join(',') : undefined,
        },
        responseType: 'blob',
      });
      const extension = format === 'pdf' ? 'pdf' : 'xlsx';
      const blobUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = `obligaciones-${querySnapshot.as_of}.${extension}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showSnack(`No se pudo exportar ${format === 'pdf' ? 'el PDF' : 'el Excel'}.`, 'error');
    } finally {
      setExporting((prev) => ({ ...prev, [format]: false }));
    }
  };

  const providerChart = useMemo(() => (charts.debt_by_supplier || []).map((item) => ({
    ...item,
    balance: Number(item.balance || 0),
  })), [charts]);
  const agingChart = useMemo(() => (charts.aging || []).map((item) => ({
    ...item,
    balance: Number(item.balance || 0),
  })), [charts]);
  const situationChart = useMemo(() => (charts.current_situation || []).map((item) => ({
    ...item,
    value: Number(item.invoices || 0),
  })), [charts]);

  const excludedSuppliers = useMemo(
    () => suppliers.filter((item) => excludedSupplierIds.includes(Number(item.id))),
    [suppliers, excludedSupplierIds]
  );
  const configurableSuppliers = useMemo(() => {
    const term = supplierConfigSearch.trim().toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter((item) => `${item.companyName || ''} ${item.legalName || ''}`.toLowerCase().includes(term));
  }, [supplierConfigSearch, suppliers]);
  const visibleSupplierOptions = useMemo(() => {
    const byId = new Map();
    rows.forEach((row) => byId.set(String(row.supplier_id), { id: String(row.supplier_id), name: row.supplier_name }));
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const filteredRows = useMemo(() => {
    const term = pageFilters.search.trim().toLowerCase();
    const from = pageFilters.dueFrom ? localDate(pageFilters.dueFrom) : '';
    const to = pageFilters.dueTo ? localDate(pageFilters.dueTo) : '';
    return rows.filter((row) => {
      if (pageFilters.supplierId && String(row.supplier_id) !== String(pageFilters.supplierId)) return false;
      if (from && String(row.due_date || '') < from) return false;
      if (to && String(row.due_date || '') > to) return false;
      if (pageFilters.states.length && !pageFilters.states.includes(row.current_financial_status)) return false;
      if (term) {
        const haystack = `${row.supplier_name || ''} ${row.number || ''} ${row.series || ''} ${row.current_financial_status_label || ''} ${row.current_status_label || ''}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [pageFilters, rows]);

  const toggleExcludedSupplier = (supplierId) => {
    const id = Number(supplierId);
    setExcludedSupplierIds((prev) => (
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    ));
  };
  const togglePageState = (statusCode) => {
    setPageFilters((prev) => ({
      ...prev,
      states: prev.states.includes(statusCode)
        ? prev.states.filter((value) => value !== statusCode)
        : [...prev.states, statusCode],
    }));
  };
  const clearPageFilters = () => setPageFilters({ supplierId: '', dueFrom: null, dueTo: null, states: [], search: '' });

  return (
    <>
      <MainText
        title="Obligaciones historicas"
        subtitle="Facturas vencidas y con saldo real al corte, conciliadas contra su situacion actual."
        icon={ReceiptLong}
        width="auto"
      />

      <Paper variant="outlined" sx={{ mt: 1.5, p: 1.75, borderRadius: 2, borderTop: '3px solid #10173c' }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} alignItems={{ lg: 'center' }}>
          <Autocomplete
            options={suppliers}
            value={supplier}
            onChange={(_, value) => setSupplier(value)}
            getOptionLabel={(option) => option?.companyName || option?.legalName || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => <TextField {...params} size="small" label="Proveedor (opcional)" />}
            sx={{ minWidth: { xs: '100%', lg: 330 } }}
          />
          <DatePick
            name="as_of"
            label="Fecha de corte"
            value={cutoff}
            onChange={setCutoff}
            size="small"
            allowHistoricalDates
            allowFutureDates={false}
            width="220px"
            minWidth={180}
            maxWidth={240}
          />
          <Button variant="contained" startIcon={<Search />} onClick={handleConsult} disabled={blockingLoading || !cutoff}>
            Consultar
          </Button>
          {!supplier ? (
            <Button variant="outlined" startIcon={<Tune />} onClick={() => setSupplierConfigOpen(true)}>
              Configurar proveedores
            </Button>
          ) : null}
          <Paper variant="outlined" sx={{ p: 0.75, ml: { lg: 'auto !important' }, minWidth: 250 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 800, color: 'text.secondary' }}>Descargar reporte</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 0.75 }}>
              <Select value={exportMode} onChange={(event) => setExportMode(event.target.value)}>
                <MenuItem value="complete">Reporte completo</MenuItem>
                <MenuItem value="visible">Solo filas visibles ({filteredRows.length})</MenuItem>
              </Select>
            </FormControl>
            {exportMode === 'visible' ? (
              <Chip size="small" color="info" label="Exportando vista filtrada" sx={{ mb: 0.75 }} />
            ) : null}
            <Stack direction="row" spacing={0.75}>
              <Tooltip title={hasConsulted ? 'Descargar PDF horizontal' : 'Consulta el reporte primero'}>
                <span style={{ flex: 1 }}>
                  <Button fullWidth size="small" variant="outlined" sx={{ color: '#b42318', borderColor: '#efb4ae' }} startIcon={exporting.pdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />} onClick={() => exportReport('pdf')} disabled={!hasConsulted || exporting.pdf || (exportMode === 'visible' && filteredRows.length === 0)}>
                    PDF
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={hasConsulted ? 'Descargar libro Excel' : 'Consulta el reporte primero'}>
                <span style={{ flex: 1 }}>
                  <Button fullWidth size="small" variant="outlined" color="success" startIcon={exporting.xlsx ? <CircularProgress size={16} color="inherit" /> : <TableView />} onClick={() => exportReport('xlsx')} disabled={!hasConsulted || exporting.xlsx || (exportMode === 'visible' && filteredRows.length === 0)}>
                    Excel
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Paper>
        </Stack>
        {!supplier && excludedSuppliers.length ? (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
            <Typography variant="caption" sx={{ alignSelf: 'center', fontWeight: 800 }}>Excluidos:</Typography>
            {excludedSuppliers.map((item) => (
              <Chip key={item.id} size="small" label={item.companyName || item.legalName} onDelete={() => toggleExcludedSupplier(item.id)} />
            ))}
            <Button size="small" onClick={() => setExcludedSupplierIds([])} startIcon={<RestartAlt />}>Restaurar todos</Button>
          </Stack>
        ) : null}
      </Paper>

      {!hasConsulted ? (
        <Paper variant="outlined" sx={{ mt: 1.5, py: 8, textAlign: 'center', borderRadius: 2 }}>
          <Search sx={{ fontSize: 44, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 800 }}>Configura el corte y pulsa Consultar</Typography>
          <Typography variant="body2" color="text.secondary">El reporte no se ejecuta automaticamente.</Typography>
        </Paper>
      ) : null}

      {hasConsulted ? (
        <>
          <Box sx={{ mt: 1.5, display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(5, 1fr)' } }}>
            <Metric label="BRUTO AL CORTE" value={money(summary.total_gross_at_cutoff)} tone="#b42318" icon={AccountBalanceWallet} />
            <Metric label="EN CONCILIACION AL CORTE" value={money(summary.total_in_reconciliation_at_cutoff)} tone="#d97706" />
            <Metric label="PAGABLE AL CORTE" value={money(summary.total_payable_at_cutoff)} tone="#7c3aed" />
            <Metric label="BRUTO ACTUAL" value={money(summary.total_current_gross)} tone="#10173c" />
            <Metric label="EN CONCILIACION ACTUAL" value={money(summary.total_current_in_reconciliation)} tone="#d97706" />
            <Metric label="PAGABLE ACTUAL" value={money(summary.total_current_payable)} tone="#2563eb" />
            <Metric label="PAGADO DESPUES" value={money(summary.total_paid_after_cutoff)} tone="#0f766e" />
            <Metric label="FACTURAS" value={Number(summary.invoice_count || 0).toLocaleString('es-GT')} tone="#2563eb" />
            <Metric label="PROVEEDORES" value={Number(summary.provider_count || 0).toLocaleString('es-GT')} tone="#7c3aed" />
            <Metric label="NO CONFIRMADAS" value={Number(summary.unconfirmed_count || 0).toLocaleString('es-GT')} tone="#d97706" />
          </Box>

          <Box sx={{ mt: 1.25, display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', xl: '1.25fr 1fr 1fr' } }}>
            <Paper variant="outlined" sx={{ p: 1.5, height: 315 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Deuda al corte por proveedor</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={providerChart} margin={{ top: 15, right: 10, left: 5, bottom: 55 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={72} tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={(value) => `Q${Number(value).toLocaleString('es-GT')}`} tick={{ fontSize: 10 }} />
                  <ChartTooltip formatter={(value) => money(value)} />
                  <Bar dataKey="balance" name="Saldo al corte" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5, height: 315 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Antiguedad de deuda</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={agingChart} margin={{ top: 15, right: 10, left: 5, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(value) => `Q${Number(value).toLocaleString('es-GT')}`} tick={{ fontSize: 10 }} />
                  <ChartTooltip formatter={(value) => money(value)} />
                  <Bar dataKey="balance" name="Saldo" fill="#d97706" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.5, height: 315 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Situacion actual de estas facturas</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie data={situationChart} dataKey="value" nameKey="label" innerRadius={48} outerRadius={78} paddingAngle={2}>
                    {situationChart.map((entry, index) => <Cell key={entry.status} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <ChartTooltip formatter={(value) => `${value} facturas`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          <Paper variant="outlined" sx={{ mt: 1.25, p: 1.25, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1} alignItems={{ xl: 'center' }}>
              <Autocomplete
                size="small"
                options={visibleSupplierOptions}
                value={visibleSupplierOptions.find((item) => item.id === String(pageFilters.supplierId)) || null}
                onChange={(_, value) => setPageFilters((prev) => ({ ...prev, supplierId: value?.id || '' }))}
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => <TextField {...params} label="Proveedor visible" />}
                sx={{ minWidth: 230 }}
              />
              <DatePick name="due_from" label="Vence desde" value={pageFilters.dueFrom} onChange={(value) => setPageFilters((prev) => ({ ...prev, dueFrom: value }))} size="small" allowHistoricalDates allowFutureDates width="180px" />
              <DatePick name="due_to" label="Vence hasta" value={pageFilters.dueTo} onChange={(value) => setPageFilters((prev) => ({ ...prev, dueTo: value }))} size="small" allowHistoricalDates allowFutureDates width="180px" />
              <TextField
                size="small"
                label="Buscar proveedor, factura, serie o estado"
                value={pageFilters.search}
                onChange={(event) => setPageFilters((prev) => ({ ...prev, search: event.target.value }))}
                sx={{ minWidth: 290, flex: 1 }}
              />
              <Button size="small" startIcon={<RestartAlt />} onClick={clearPageFilters}>Limpiar filtros</Button>
            </Stack>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {Object.entries(financialStatusLabels).map(([code, label]) => (
                <Chip key={code} clickable size="small" color={pageFilters.states.includes(code) ? 'primary' : 'default'} variant={pageFilters.states.includes(code) ? 'filled' : 'outlined'} label={label} onClick={() => togglePageState(code)} onDelete={pageFilters.states.includes(code) ? () => togglePageState(code) : undefined} />
              ))}
              {pageFilters.supplierId ? <Chip size="small" label={`Proveedor: ${visibleSupplierOptions.find((item) => item.id === String(pageFilters.supplierId))?.name || ''}`} onDelete={() => setPageFilters((prev) => ({ ...prev, supplierId: '' }))} /> : null}
              {pageFilters.dueFrom ? <Chip size="small" label={`Desde: ${formatDate(localDate(pageFilters.dueFrom))}`} onDelete={() => setPageFilters((prev) => ({ ...prev, dueFrom: null }))} /> : null}
              {pageFilters.dueTo ? <Chip size="small" label={`Hasta: ${formatDate(localDate(pageFilters.dueTo))}`} onDelete={() => setPageFilters((prev) => ({ ...prev, dueTo: null }))} /> : null}
              {pageFilters.search ? <Chip size="small" label={`Busqueda: ${pageFilters.search}`} onDelete={() => setPageFilters((prev) => ({ ...prev, search: '' }))} /> : null}
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 'auto' }}>{filteredRows.length} de {rows.length} filas de esta pagina</Typography>
            </Stack>
          </Paper>

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1.25, borderRadius: 2, maxHeight: 'calc(100vh - 280px)' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 3150 }}>
              <TableHead>
                <TableRow>
                  {[
                    'Proveedor', 'Factura / serie', 'Fecha de factura', 'Fecha de recepción', 'Vencimiento', 'Total',
                    'Estado financiero al corte', 'Bruto al corte', 'En conciliacion al corte', 'Pagable al corte',
                    'Estado financiero actual', 'Bruto actual', 'En conciliacion actual', 'Pagable actual', 'Situacion posterior', 'Pagado despues',
                    'Estado factura', 'Firma', 'Fuente confirmacion', 'Flujo', 'Estado bancario', 'Detalle',
                  ].map((label) => (
                    <TableCell key={label} align={['Total', 'Bruto al corte', 'En conciliacion al corte', 'Pagable al corte', 'Bruto actual', 'En conciliacion actual', 'Pagable actual', 'Pagado despues'].includes(label) ? 'right' : 'left'} sx={{ bgcolor: '#10173c', color: '#fff', fontWeight: 800, fontSize: 11, whiteSpace: 'nowrap' }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pageLoading ? Array.from({ length: Math.min(pagination.page_size || 10, 10) }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>{Array.from({ length: 22 }).map((__, cell) => <TableCell key={cell}><Skeleton /></TableCell>)}</TableRow>
                )) : null}
                {!pageLoading && filteredRows.length === 0 ? (
                  <TableRow><TableCell colSpan={22} align="center" sx={{ py: 7, color: 'text.secondary' }}>No hay facturas que cumplan las reglas del corte y los filtros visibles.</TableCell></TableRow>
                ) : null}
                {!pageLoading && filteredRows.map((row) => (
                  <TableRow hover key={row.id}>
                    <TableCell sx={{ minWidth: 190, fontWeight: 700 }}>{row.supplier_name}</TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 800 }}>#{row.number}</Typography><Typography variant="caption" color="text.secondary">{row.series || 'Sin serie'}</Typography></TableCell>
                    <TableCell>{formatDate(row.invoice_date)}</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      {getReceiptDateState(row.receipt_date, querySnapshot?.as_of).isMissing ? (
                        <Typography variant="caption" color="text.secondary">Sin recepción registrada</Typography>
                      ) : (
                        <Stack spacing={0.35} alignItems="flex-start">
                          <Typography variant="body2">{formatDate(row.receipt_date)}</Typography>
                          {getReceiptDateState(row.receipt_date, querySnapshot?.as_of).isAfterCutoff ? (
                            <Chip size="small" color="warning" variant="outlined" label="Posterior al corte" />
                          ) : null}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(row.due_date)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{money(row.total_effective)}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" color={row.financial_status_at_cutoff === 'AWAITING_RECONCILIATION' ? 'warning' : 'info'} label={row.financial_status_at_cutoff_label} /></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 850, color: 'error.main' }}>{money(row.gross_balance_at_cutoff)}</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main', fontWeight: 750 }}>{money(row.in_reconciliation_at_cutoff)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 850 }}>{money(row.payable_balance_at_cutoff)}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" color={row.current_financial_status === 'PAID' ? 'success' : row.current_financial_status === 'AWAITING_RECONCILIATION' ? 'warning' : 'info'} label={row.current_financial_status_label} /></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{money(row.current_gross_balance)}</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main', fontWeight: 750 }}>{money(row.current_in_reconciliation)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{money(row.current_payable_balance)}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" color={currentStatusColor[row.current_status] || 'default'} label={row.current_status_label} /></TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 750 }}>{money(row.paid_after_cutoff)}</TableCell>
                    <TableCell sx={{ minWidth: 205 }}>{row.invoice_status_label}</TableCell>
                    <TableCell><Chip size="small" color={row.is_signed ? 'success' : 'default'} variant="outlined" label={row.signed_label} /></TableCell>
                    <TableCell sx={{ minWidth: 165 }}>{row.confirmed_source_label}</TableCell>
                    <TableCell>{row.payment_flow_label}</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>{row.bank_payment_status_label || 'No aplica'}</TableCell>
                    <TableCell><Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => openDetail(row)}>Ver detalle</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Paper variant="outlined" sx={{ borderTop: 0, borderRadius: '0 0 8px 8px' }}>
            <TablePagination
              component="div"
              count={pagination.total_items || 0}
              page={Math.max((pagination.page || 1) - 1, 0)}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.page_size || 25}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Filas por pagina"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              disabled={pageLoading}
            />
          </Paper>
        </>
      ) : null}

      <Dialog open={supplierConfigOpen} onClose={() => setSupplierConfigOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Configurar proveedores</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            Todos los proveedores del flujo normal se incluyen por defecto. Desmarca únicamente los que no deben formar parte de la consulta ni de sus exportaciones.
          </Typography>
          <TextField fullWidth size="small" label="Buscar proveedor" value={supplierConfigSearch} onChange={(event) => setSupplierConfigSearch(event.target.value)} sx={{ mb: 1 }} />
          <Stack sx={{ maxHeight: 390, overflowY: 'auto' }}>
            {configurableSuppliers.map((item) => (
              <FormControlLabel
                key={item.id}
                control={<Checkbox checked={!excludedSupplierIds.includes(Number(item.id))} onChange={() => toggleExcludedSupplier(item.id)} />}
                label={item.companyName || item.legalName || `Proveedor ${item.id}`}
              />
            ))}
            {!configurableSuppliers.length ? <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Sin coincidencias.</Typography> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<RestartAlt />} onClick={() => setExcludedSupplierIds([])}>Incluir todos</Button>
          <Button variant="contained" onClick={() => setSupplierConfigOpen(false)}>Listo</Button>
        </DialogActions>
      </Dialog>

      <QueryBackdrop open={blockingLoading} />
      <DetailDialog
        open={detailState.open}
        loading={detailState.loading}
        detail={detailState.data}
        asOf={querySnapshot?.as_of}
        onClose={() => setDetailState({ open: false, loading: false, data: null })}
      />
      <CustomSnackBar
        open={snack.open}
        setOpen={(open) => setSnack((prev) => ({ ...prev, open }))}
        message={snack.message}
        severity={snack.severity}
      />
    </>
  );
}
